/**
 * GA4 Parameter & URL Sanitizer
 * Strips PII (emails, phones), credentials, tokens, and sensitive query parameters.
 */

// Common sensitive query parameters to strip from URLs
export const SENSITIVE_QUERY_PARAMS = new Set([
  'token',
  'auth',
  'key',
  'api_key',
  'apikey',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'id_token',
  'password',
  'pwd',
  'pass',
  'code',
  'secret',
  'session',
  'session_id',
  'sessionid',
  'jwt',
  'email',
  'user_email',
  'phone',
  'tel',
  'card',
  'cvv',
]);

// Sensitive keys in event parameter objects
export const SENSITIVE_PARAM_KEYS = new Set([
  'password',
  'pwd',
  'pass',
  'passwd',
  'secret',
  'token',
  'auth',
  'authorization',
  'credit_card',
  'card_number',
  'cvv',
  'ssn',
  'user_email',
  'customer_email',
  'email',
  'phone',
  'phone_number',
  'customer_phone',
  'pin',
  'access_token',
  'refresh_token',
]);

// Regex for email detection
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// Regex for phone numbers with at least 7 consecutive/formatted digits
const PHONE_REGEX = /(\+?\d{1,4}[ -]?)?(\(?\d{2,4}\)?[ -]?)?\d{3,4}[ -]?\d{3,4}/g;

/**
 * Sanitizes a URL by removing sensitive query parameters and redacting embedded PII.
 */
export function sanitizeUrl(urlStr: string): string {
  if (!urlStr || typeof urlStr !== 'string') return '';

  try {
    // Handle both relative paths and full URLs
    const isRelative = !urlStr.startsWith('http://') && !urlStr.startsWith('https://');
    const dummyBase = 'https://sewargourmet.com';
    const parsed = new URL(urlStr, dummyBase);

    // Filter out sensitive query parameters
    const keysToRemove: string[] = [];
    parsed.searchParams.forEach((_, key) => {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach((k) => parsed.searchParams.delete(k));

    // Redact any remaining PII in the search string
    let search = parsed.search;
    if (search) {
      search = search.replace(EMAIL_REGEX, '[REDACTED_EMAIL]');
    }

    if (isRelative) {
      return parsed.pathname + search + parsed.hash;
    }

    return parsed.origin + parsed.pathname + search + parsed.hash;
  } catch {
    // Fallback: simple string replacement if URL parsing fails
    return urlStr
      .replace(EMAIL_REGEX, '[REDACTED_EMAIL]')
      .split('?')[0]; // Strip all query params in fallback
  }
}

/**
 * Redacts string content containing email addresses or sensitive credentials.
 */
export function sanitizeString(val: string): string {
  if (!val || typeof val !== 'string') return val;
  let sanitized = val.replace(EMAIL_REGEX, '[REDACTED_EMAIL]');
  // Also truncate overly long strings to comply with GA4 limits (100 chars for standard parameter values)
  if (sanitized.length > 500) {
    sanitized = sanitized.slice(0, 500);
  }
  return sanitized;
}

/**
 * Recursively sanitizes an event parameters object:
 * - Drops disallowed keys (passwords, tokens, raw form fields)
 * - Redacts emails and phone numbers from values
 * - Enforces GA4 limits and primitive types
 */
export function sanitizeParameters(params: Record<string, any> = {}): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [rawKey, rawValue] of Object.entries(params)) {
    const keyLower = rawKey.toLowerCase().trim();

    // 1. Drop sensitive keys completely
    if (SENSITIVE_PARAM_KEYS.has(keyLower)) {
      continue;
    }

    // Drop keys starting with sensitive prefixes
    if (
      keyLower.includes('password') ||
      keyLower.includes('secret') ||
      keyLower.includes('token')
    ) {
      continue;
    }

    // 2. Handle values based on type
    if (rawValue === null || rawValue === undefined) {
      continue;
    }

    if (typeof rawValue === 'string') {
      result[rawKey] = sanitizeString(rawValue);
    } else if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
      result[rawKey] = rawValue;
    } else if (Array.isArray(rawValue)) {
      // For arrays (e.g., items array in ecommerce), sanitize each item
      result[rawKey] = rawValue
        .filter((item) => item !== null && item !== undefined)
        .map((item) => (typeof item === 'object' ? sanitizeParameters(item) : sanitizeString(String(item))));
    } else if (typeof rawValue === 'object') {
      result[rawKey] = sanitizeParameters(rawValue);
    }
  }

  return result;
}

/**
 * Validates that an event name adheres to GA4 syntax rules.
 */
export function isValidEventName(eventName: string): boolean {
  if (!eventName || typeof eventName !== 'string') return false;
  // GA4 event names: alphanumeric + underscores, up to 40 characters, cannot start with a number
  const regex = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
  return regex.test(eventName);
}
